import { KernelConfig, ParamSelection, PolygonSelection, ExampleEntry, PhaseData } from "../types/lenia";
import { formatNumber } from "../utils/numberUtils";
import { RAW_TO_RUN_PHASE, PHASE_FROM_COMPACT_ID } from "../constants/phases";

const DATASET_DATA_URL = process.env.REACT_APP_DATASET_DATA_URL!;
const DATASET_TREE_URL = process.env.REACT_APP_DATASET_TREE_URL!;

export interface InitPattern {
  grid: number;
  state: number[][];
}

async function fetchJson<T>(url: string, signal?: AbortSignal): Promise<T> {
  const resp = await fetch(url, { cache: "default", signal });
  if (!resp.ok) throw new Error(`Failed to fetch: ${url} (${resp.status})`);
  return resp.json() as Promise<T>;
}

const isGaussKernel = (
  k: KernelConfig
): k is KernelConfig & { funcK: "gauss"; muK: number; sigmaK: number } =>
  k.funcK === "gauss" && typeof k.muK === "number" && typeof k.sigmaK === "number";

const fmtBetas = (betas: number[]) => betas.map(b => formatNumber(b)).join(",");

const gaussPartOf = (k: KernelConfig) =>
  isGaussKernel(k) ? `${formatNumber(k.muK)}_${formatNumber(k.sigmaK)}` : "";

/** Stable ID used for file/dir names (mirrors your dataset layout). */
export const coreKernelId = (k: KernelConfig) => {
  const parts = [k.funcK];
  const g = gaussPartOf(k);
  if (g) parts.push(g);
  parts.push(fmtBetas(k.betas));
  return parts.join("_");
};

export const kernelId = (k: KernelConfig) => {
  const core = coreKernelId(k);
  return `${core}_${k.kSize}_${k.gridSize}_${k.tMax}`;
};

/** Public URL to preview image for a kernel (no fetch). */
export function kernelImageUrl(k: KernelConfig): string {
  return `${DATASET_DATA_URL}/kernel_img/${coreKernelId(k)}_${k.kSize}.png`;
}

/** Human-friendly one-liner for titles/tooltips. */
export function kernelDescription(k: KernelConfig): string {
  const muSig =
    k.funcK === "gauss"
      ? ` · μₖ=${formatNumber(k.muK!)} · σₖ=${formatNumber(k.sigmaK!)}`
      : "";
  const betas = ` · β=[${k.betas.join(",")}]`;
  const r = ` · R=${k.kSize}`;
  const tMax = ` · tₘₐₓ=${k.tMax}`;
  return `fₖ=${k.funcK}${muSig}${betas}`;
}

const phaseFilename = (k: KernelConfig) => `phases/${kernelId(k)}.json`;
const kernelDirPath = (k: KernelConfig) => `kernels/${kernelId(k)}`;

/** URL where per-(mu,sigma) JSON lives for a kernel */
const selectionUrl = (k: KernelConfig, params: ParamSelection) =>
  `${DATASET_DATA_URL}/${kernelDirPath(k)}/mu_${params.mu}/sigma_${params.sigma}.json`;

/** Pick the largest numeric key in an object and return [key, value] */
function pickLatestNumericBucket<T = any>(obj: Record<string, T>): [number, T] {
  const numericKeys = Object.keys(obj)
    .map(k => parseInt(k, 10))
    .filter(k => !Number.isNaN(k))
    .sort((a, b) => a - b);
  if (!numericKeys.length) throw new Error("No numeric grid keys found");
  const grid = numericKeys[numericKeys.length - 1];
  return [grid, obj[String(grid)] as T];
}

/** ---------- kernel list & parser ---------- */

type TreeItem = { type: string; path: string };

/**
 * Directory name formats supported:
 *  - Gauss:   gauss_<mu>_<sigma>_<beta,beta,...>_<kSize>_<gridSize>_<tMax>
 *  - Others:  <func>_<beta,beta,...>_<kSize>_<gridSize>_<tMax>
 * Only kSize and tMax are used in the kernelId; gridSize is present in KernelConfig
 * but the per-(mu,sigma) dataset contains multiple grids under numeric keys.
 */
export async function listKernels(signal?: AbortSignal): Promise<KernelConfig[]> {
  const data = await fetchJson<Array<TreeItem>>(`${DATASET_TREE_URL}/kernels`, signal);

  const kernels = data
    .filter(item => item.type === "directory")
    .map(item => {
      const name = item.path.split("/").pop();
      if (!name) return null;

      const parts = name.split("_");
      if (parts.length < 5) return null; // minimal sanity (non-gauss)

      const funcK = parts[0];
      const isGauss = funcK === "gauss";

      try {
        if (isGauss) {
          // gauss_<mu>_<sigma>_<betas>_<kSize>_<gridSize>_<tMax>
          if (parts.length < 7) return null;
          const muK = Number(parts[1]);
          const sigmaK = Number(parts[2]);
          const betas = parts[3].split(",").map(Number);
          const kSize = parseInt(parts[4], 10);
          const gridSize = parseInt(parts[5], 10);
          const tMax = parseInt(parts[6], 10);

          if (
            [muK, sigmaK, ...betas, kSize, gridSize, tMax].some(n =>
              Number.isNaN(n as number)
            )
          )
            return null;

          const k: KernelConfig = {
            funcK: "gauss",
            muK,
            sigmaK,
            betas,
            kSize,
            gridSize,
            tMax,
          } as KernelConfig;

          return k;
        } else {
          // <func>_<betas>_<kSize>_<gridSize>_<tMax>
          if (parts.length < 5) return null;
          const betas = parts[1].split(",").map(Number);
          const kSize = parseInt(parts[2], 10);
          const gridSize = parseInt(parts[3], 10);
          const tMax = parseInt(parts[4], 10);

          if ([...betas, kSize, gridSize, tMax].some(n => Number.isNaN(n)))
            return null;

          const k: KernelConfig = {
            funcK,
            betas,
            kSize,
            gridSize,
            tMax,
          } as KernelConfig;

          return k;
        }
      } catch {
        return null;
      }
    })
    .filter((k): k is KernelConfig => k !== null);

  return kernels;
}

/** ---------- phase grid loader ---------- */
export async function loadPhaseGrid(
  kernel: KernelConfig,
  signal?: AbortSignal
): Promise<PhaseData[]> {
  const url = `${DATASET_DATA_URL}/${phaseFilename(kernel)}`;
  const data = await fetchJson<{ p: [number, number, number][] }>(url, signal);

  // Convert compact [mu, sigma, id] into PhaseData objects
  return (data.p ?? []).map(([mu, sigma, id]) => ({
    mu,
    sigma,
    phase_category: PHASE_FROM_COMPACT_ID[id],
  }));
}

/** ---------- deterministic default selection ---------- */
export function pickInitialSelection(
  phaseData: PhaseData | PhaseData[] | null
): ParamSelection | null {
  if (!phaseData || (Array.isArray(phaseData) && phaseData.length === 0)) return null;
  const arr = Array.isArray(phaseData) ? phaseData : [phaseData];

  const isSoliton = (p: PhaseData) => String(p.phase_category).toLowerCase() === "solitons";
  const solitonPoints = arr
    .filter(isSoliton)
    .sort((a, b) => a.mu - b.mu || a.sigma - b.sigma);

  if (solitonPoints.length) {
    const s = solitonPoints[0];
    return { mu: s.mu, sigma: s.sigma };
  }

  const minMu = Math.min(...arr.map(p => p.mu));
  const atMinMu = arr.filter(p => Math.abs(p.mu - minMu) < 1e-9);
  const minSig = Math.min(...atMinMu.map(p => p.sigma));
  return { mu: minMu, sigma: minSig };
}

/** ---------- initial pattern loader ---------- */
export async function loadInitialPattern(
  kernel: KernelConfig,
  params: ParamSelection,
  polygonSelection: PolygonSelection,
  pyodide: any,
  signal?: AbortSignal
): Promise<InitPattern> {
  const datasetUrl = selectionUrl(kernel, params);
  const dataset = await fetchJson<Record<string, any>>(datasetUrl, signal);

  const [grid, polys] = pickLatestNumericBucket<Record<string, any>>(dataset);
  const entry = polys[String(polygonSelection.polygon_size)];
  if (!entry) throw new Error(`No entry for poly_size=${polygonSelection.polygon_size}`);

  const { sample, seed } = entry;
  const i = polygonSelection.sample_index;
  if (i < 0 || i >= sample.length) throw new Error(`Invalid sample_index ${i}`);

  const sampleId = sample[i];
  const seedVal = seed[i];

  const polygonsUrl = `${DATASET_DATA_URL}/polygons/polygons${grid}/size_${polygonSelection.polygon_size}/id_${sampleId}.json`;
  const polygons = await fetchJson<Record<string, number[][]>>(polygonsUrl, signal);

  const key = `poly_${polygonSelection.polygon_size}_${sampleId}`;
  const rawMask: number[][] = polygons[key];
  if (!rawMask) throw new Error(`Mask not found: ${key}`);

  await pyodide.globals.set("raw_mask_js", rawMask);
  await pyodide.globals.set("grid", grid);
  await pyodide.globals.set("seed_val", seedVal);

  const state: number[][] = await (
    await pyodide.runPythonAsync(`
import numpy as np
def load_pattern(pattern, array_sizes):
    array_mids = [size // 2 for size in array_sizes]
    array = np.zeros([1] + array_sizes)
    pattern = np.asarray(pattern)
    _, w, h = pattern.shape
    x1 = array_mids[0] - w//2;  x2 = x1 + w
    y1 = array_mids[1] - h//2;  y2 = y1 + h
    array[:, x1:x2, y1:y2] = pattern
    return array
raw_mask = np.array(raw_mask_js, dtype=np.float64)
h, w = raw_mask.shape
H = W = int(grid)
pattern = raw_mask.reshape(1, h, w)
mask = load_pattern(pattern, [H, W]).reshape(H, W)
np.random.seed(int(seed_val))
rand = np.random.rand(1, H, W)[0]
state = (rand * mask).tolist()
state
    `)
  ).toJs({ create_proxies: false });

  return { grid, state };
}

/** ---------- polygon phases ---------- */
export async function loadPolygonPhases(
  kernel: KernelConfig,
  params: ParamSelection,
  signal?: AbortSignal
): Promise<Record<number, ExampleEntry[]>> {
  const datasetUrl = selectionUrl(kernel, params);
  const dataset = await fetchJson<Record<string, any>>(datasetUrl, signal);
  const result: Record<number, ExampleEntry[]> = {};

  for (const [gridSize, polys] of Object.entries(dataset)) {
    if (gridSize === "grid_size") continue;

    for (const [polySizeStr, entry] of Object.entries<any>(polys)) {
      const polySize = parseInt(polySizeStr, 10);
      if (Number.isNaN(polySize)) continue;

      const phases: any[] = (entry as any).phase ?? [];
      if (!Array.isArray(phases) || phases.length === 0) continue;

      const exampleList = phases
        .map((ph, idx) => {
          const norm = RAW_TO_RUN_PHASE[String(ph)];
          if (!norm) return null;
          return { sample_index: idx, phase: norm } as ExampleEntry;
        })
        .filter(Boolean) as ExampleEntry[];

      if (exampleList.length) result[polySize] = exampleList;
    }
  }

  return result;
}
